import { Moment } from 'moment';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DateRanges,
  Focus,
  FocusPosition,
  MinMaxMoment,
  MomentRange,
  MomentRanges,
  RangeIndex,
} from './types';
import { momentRangeToDateRange, updateRangeEnd, updateRangeStart } from './util';

/**
 * Constructs the preview range with respect to the currently hovered day and the
 * focused range/position.
 */
export const usePreviewRange = (
  hoveredDay: Moment | null,
  focusPosition: FocusPosition,
  focusedRange: MomentRange,
) => {
  const previewRange = useMemo(() => {
    // if no day is hovered, there is no preview range
    if (hoveredDay === null) return undefined;
    const isStart = focusPosition === FocusPosition.Start;
    // preview range should just be the hovered day if:
    //  - the focus position is start AND the start day is already set OR the end day is unset
    //  - the focus position is end AND the start day is unset
    if (isStart ? focusedRange.start || !focusedRange.end : !focusedRange.start) {
      return { start: hoveredDay, end: hoveredDay };
    }
    // if the focus position is end, the preview range will extend from the start day
    // to the hovered day, whichever direction that may be

    return isStart
      ? updateRangeStart(focusedRange, hoveredDay)
      : updateRangeEnd(focusedRange, hoveredDay);
  }, [hoveredDay, focusPosition, focusedRange]);
  return previewRange;
};

type InputRef = {
  focus: (position: FocusPosition) => void;
  blur: (position: FocusPosition) => void;
};

interface UseDateRangeFocusProps {
  ranges: MomentRanges;
  onChange: (ranges: DateRanges) => void;
  disableComparison: boolean;
  minMaxMoment: MinMaxMoment;
  ongoing?: boolean;
  preserveRangeEnd: boolean;
  includeTimeInputs?: boolean;
}

/**
 * Maintains state of which date range input is focused. Returns said state, a function to
 * update the focus state when an input is focused, and a function that can be used to
 * focus the next input.
 */
export const useDateRangeFocus = ({
  ranges,
  onChange,
  disableComparison,
  minMaxMoment,
  ongoing,
  preserveRangeEnd,
  includeTimeInputs,
}: UseDateRangeFocusProps) => {
  const [focusedRange, setFocusedRange] = useState(RangeIndex.Primary);
  const [focusPosition, setFocusPosition] = useState(FocusPosition.Start);
  const focus: Focus = useMemo(() => [focusedRange, focusPosition], [focusedRange, focusPosition]);
  const inputRefs = useMemo(
    () => [
      createRef<InputRef>(),
      createRef<InputRef>(),
      createRef<InputRef>(),
      createRef<InputRef>(),
    ],
    [],
  );

  const focusRangeInput = useCallback(
    (focusedRange: RangeIndex, focusPosition: FocusPosition) => {
      const rangeRef = inputRefs[focusedRange];
      rangeRef?.current?.focus(focusPosition);
    },
    [inputRefs],
  );

  // When `focusedRange` or `focusPosition` changes, focus the corresponding input.
  useEffect(
    () => focusRangeInput(focusedRange, focusPosition),
    [focusedRange, focusPosition, focusRangeInput],
  );

  // Programmatically focuses the next input based on the current state of things.
  const focusNext = useCallback(() => {
    let range = focusedRange;
    let position = focusPosition;

    // blur the current input to submit the data
    inputRefs[range]?.current?.blur(position);

    // if the time inputs are enabled, the comparison end time is disabled
    const endOfComparison = !includeTimeInputs ? FocusPosition.End : FocusPosition.EndTime;

    // if the date range is ongoing, the primary end date is disabled
    const endOfPrimary = ongoing
      ? !includeTimeInputs
        ? FocusPosition.Start
        : FocusPosition.StartTime
      : endOfComparison;

    const maxPosition = range === RangeIndex.Primary ? endOfPrimary : endOfComparison;

    // advance the position
    position = Math.min(++position, maxPosition);

    // skip over time inputs by default
    if (position !== FocusPosition.EndTime) {
      if (position === FocusPosition.StartTime) {
        position = Math.min(++position, maxPosition);
      }

      if (position !== focusPosition && position === FocusPosition.End) {
        return focusRangeInput(range, position);
      }
    }
    // we reached the end of the range, see if we need to switch ranges
    if (disableComparison) {
      // if comparison is disabled, circle back
      return focusRangeInput(range, FocusPosition.Start);
    }

    // if the start of the range hasn't been set and we aren't already on it, focus it
    if (!ranges[range].start && position !== FocusPosition.Start) {
      return focusRangeInput(range, FocusPosition.Start);
    }

    // advance the range
    range = Math.min(++range, RangeIndex.Comparison);

    if (range !== focusedRange) {
      // start at the beginning of the new range
      return focusRangeInput(range, FocusPosition.Start);
    }

    // otherwise, cycle around
    focusRangeInput(RangeIndex.Primary, FocusPosition.Start);
  }, [
    disableComparison,
    focusPosition,
    focusRangeInput,
    focusedRange,
    includeTimeInputs,
    inputRefs,
    ongoing,
    ranges,
  ]);

  // Update the focus state when an input is focused.
  const onFocus = useCallback(([focusedRange, focusPosition]: Focus) => {
    setFocusedRange(focusedRange);
    setFocusPosition(focusPosition);
  }, []);

  // When a day is clicked, this updates the ranges based on the current state of things.
  const onDayChange = useDayChangeHandler(
    ranges,
    useCallback(
      (ranges, preventNext) => {
        const primaryRange = momentRangeToDateRange(ranges[RangeIndex.Primary], preserveRangeEnd);
        const comparisonRange = momentRangeToDateRange(
          ranges[RangeIndex.Comparison],
          preserveRangeEnd,
        );
        onChange(disableComparison ? [primaryRange] : [primaryRange, comparisonRange]);
        !preventNext && focusNext();
      },
      [preserveRangeEnd, onChange, disableComparison, focusNext],
    ),
    focus,
    minMaxMoment,
    ongoing,
  );

  return {
    focus,
    inputRefs,
    focusNext,
    onDayChange,
    onFocus,
  };
};

/**
 * Returns a handler that calls the onChange handler with updated ranges with respect
 * to the selected Moment and focused range/position.
 */
const useDayChangeHandler = (
  ranges: MomentRanges,
  onChange: (ranges: MomentRanges, preventNext?: boolean) => void,
  [focusedRange, focusPosition]: Focus,
  minMaxMoment: MinMaxMoment,
  ongoing?: boolean,
) => {
  const updateRange = useCallback(
    (range: MomentRange, day: Moment) =>
      focusPosition === FocusPosition.Start || focusPosition === FocusPosition.StartTime
        ? updateRangeStart(range, day, minMaxMoment, ongoing)
        : updateRangeEnd(range, day, minMaxMoment),
    [focusPosition, minMaxMoment, ongoing],
  );

  return useCallback(
    (day: Moment, preventNext?: boolean) => {
      const [primaryRange, comparisonRange] = ranges;
      onChange(
        focusedRange === RangeIndex.Primary
          ? [updateRange(primaryRange, day), comparisonRange]
          : [primaryRange, updateRange(comparisonRange, day)],
        preventNext,
      );
    },
    [focusedRange, onChange, ranges, updateRange],
  );
};
